import React, { useRef, useLayoutEffect } from "react";
import PropTypes from "prop-types";

import { useViewport } from "../../hooks/useViewport";

import { productMenu, homePath } from "../../data/links";

import NpHeaderLogo from "./images/NpHeaderLogo";

import { OverflowMenu } from "../OverflowMenu";
import { ProfileMenu } from "../ProfileMenu";
import DefaultSearch from "./DefaultSearch";

import { Col, Flexbox, colors } from "../shared";

import {
    animateScrollingDown,
    animateScrollingBackUp,
    scrollPosAnimation,
    scrollPosShowHideSearch,
    scrollSearchBarScrollingDown,
    scrollSearchBarScrollingUp
} from "./helpers";

import {
    NonMobileContentWrapper,
    TextLinkGrid,
    NonMobileTopRow,
    MainLinksCol,
    ProfileMenuCol,
    SearchContainerRow,
    MainLinks,
    Link,
    HeaderLogoLink,
    Logo,
    SearchWrapper
} from "./styles";

const NonMobileContent = ({
    transparent,
    currentUser,
    loginLink,
    joinLink,
    handleLogOut,
    search,
    isHeroSearch,
    heroSearchBgColor,
    isHeroSearchV2,
    siteHeaderRef
}) => {
    const navRef = useRef(null);
    const navLinkRef = useRef(null);
    const logoRef = useRef(null);
    const profileMenuRef = useRef(null);
    const searchContainerRef = useRef(null);
    const searchWrapperRef = useRef(null);

    // needed to adjust the scroll position for header animations for each breakpoint:
    const { browserWidth } = useViewport();

    const scrollPositionAnimation = scrollPosAnimation({
        browserWidth,
        isHeroSearchV2
    });

    const scrollPositionShowHideHeroSearch = scrollPosShowHideSearch({
        browserWidth,
        isHeroSearchV2
    });

    const handleScroll = () => {
        if (window.pageYOffset > scrollPositionAnimation) {
            animateScrollingDown({
                navRef,
                searchContainerRef,
                navLinkRef,
                profileMenuRef,
                isHeroSearch,
                heroSearchBgColor,
                siteHeaderRef,
                isHeroSearchV2
            });
        } else if (window.pageYOffset < scrollPositionAnimation) {
            animateScrollingBackUp({
                navRef,
                searchContainerRef,
                navLinkRef,
                profileMenuRef,
                isHeroSearch,
                heroSearchBgColor,
                siteHeaderRef,
                isHeroSearchV2
            });
        }
    };

    const handleScrollSearchBar = () => {
        if (searchWrapperRef.current && searchWrapperRef.current.style.display !== 'block' && location.pathname === '/') {
            searchWrapperRef.current.setAttribute('style', 'display:block');
        }

        const heroSearchWrapper = document.getElementById(
            "hero-search-wrapper"
        );

        if (window.pageYOffset > scrollPositionShowHideHeroSearch) {
            scrollSearchBarScrollingDown({
                heroSearchWrapper,
                isHeroSearchV2,
                searchWrapperRef
            });
        } else if (window.pageYOffset < scrollPositionShowHideHeroSearch) {
            scrollSearchBarScrollingUp({
                heroSearchWrapper,
                isHeroSearchV2,
                searchWrapperRef
            });
        }
    };

    useLayoutEffect(() => {
        if (searchWrapperRef.current && searchWrapperRef.current.style.display !== 'none' && location.pathname === '/') {
            searchWrapperRef.current.setAttribute('style', 'display:none');
        }
        window.addEventListener("scroll", handleScroll);
        window.addEventListener("scroll", handleScrollSearchBar);

        return () => {
            window.removeEventListener("scroll", () => handleScroll);
            window.removeEventListener("scroll", () => handleScrollSearchBar);
        };
    }, []);

    return (
        <NonMobileContentWrapper>
            <TextLinkGrid>
                <NonMobileTopRow between="md" middle="md">
                    <MainLinksCol lg={5} md={7} ref={navRef}>
                        <MainLinks
                            alignItems="center"
                            transparent={transparent}
                            ref={navLinkRef}
                        >
                            {productMenu.map(link => (
                                <Link
                                    key={link.name}
                                    href={link.href}
                                    target={link.external ? "_blank" : "_self"}
                                >
                                    {link.name}
                                </Link>
                            ))}

                            <OverflowMenu
                                transparent={transparent}
                                isHeroSearch={isHeroSearch}
                                isHeroSearchV2={isHeroSearchV2}
                            />
                        </MainLinks>
                    </MainLinksCol>
                    <ProfileMenuCol
                        mdOffset={3}
                        md={2}
                        lgOffset={6}
                        lg={1}
                        ref={profileMenuRef}
                    >
                        <Flexbox justifyContent="flex-end">
                            <ProfileMenu
                                isHeroSearch={isHeroSearch}
                                isHeroSearchV2={isHeroSearchV2}
                                currentUser={currentUser}
                                loginLink={loginLink}
                                joinLink={joinLink}
                                handleLogOut={handleLogOut}
                            />
                        </Flexbox>
                    </ProfileMenuCol>
                </NonMobileTopRow>
                <SearchContainerRow
                    middle="md"
                    ref={searchContainerRef}
                    isHeroSearch={isHeroSearch}
                    isHeroSearchV2={isHeroSearchV2}
                >
                    <Col md={2}>
                        <HeaderLogoLink href={homePath.url} ref={logoRef}>
                            {isHeroSearchV2 ? (
                                <NpHeaderLogo
                                    title={homePath.alt}
                                    fillColor={colors.whiteDuplicate}
                                />
                            ) : (
                                <Logo alt={homePath.alt} />
                            )}
                            {/* ^^ this ternary is here to isolate the new header changes so that it doesnt affect what we already have in place until it's time to refactor the header, at which point we will only import NpHeaderLogo and do away with Logo */}
                        </HeaderLogoLink>
                    </Col>
                    <Col md={8}>
                        <SearchWrapper ref={searchWrapperRef}>
                            {!isHeroSearch && <DefaultSearch search={search} />}
                        </SearchWrapper>
                    </Col>
                </SearchContainerRow>
            </TextLinkGrid>
        </NonMobileContentWrapper>
    );
};

NonMobileContent.defaultProps = {
    isHeroSearch: false
};

NonMobileContent.propTypes = {
    isHeroSearch: PropTypes.bool,
    isHeroSearchV2: PropTypes.bool,
    heroSearchBgColor: PropTypes.string,
    transparent: PropTypes.bool,
    currentUser: PropTypes.object,
    loginLink: PropTypes.oneOfType([PropTypes.node, PropTypes.element])
        .isRequired,
    joinLink: PropTypes.oneOfType([PropTypes.node, PropTypes.element])
        .isRequired,
    handleLogOut: PropTypes.func.isRequired,
    search: PropTypes.oneOfType([PropTypes.node, PropTypes.element])
};

export { NonMobileContent };
